import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import {Location} from '../../models/location';
import {catchError, tap} from 'rxjs/operators';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {BreadCrumb} from '../../models/breadcrumb';
import {from, Observable, Subscription, throwError} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
    collection,
    collectionData,
    deleteDoc,
    doc,
    docData,
    DocumentData,
    DocumentReference,
    Firestore,
    getDocs,
    orderBy,
    query,
    where
} from '@angular/fire/firestore';
import {Auth, authState} from '@angular/fire/auth';
import {BreadcrumbsComponent} from '../breadcrumbs/breadcrumbs.component';
import {ProgressComponent} from '../progress/progress.component';
import {MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle} from '@angular/material/card';
import {I18nPluralPipe} from '@angular/common';
import {MatButton, MatMiniFabButton} from '@angular/material/button';
import {MatInput} from '@angular/material/input';
import {DateFormatPipe} from '../../pipes/date-format.pipe';
import {MatFormField} from '@angular/material/form-field';

@Component({
    selector: 'app-locations',
    templateUrl: './locations.component.html',
    styleUrls: ['./locations.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0', display: 'none'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [
        BreadcrumbsComponent,
        ProgressComponent,
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardSubtitle,
        RouterLink,
        I18nPluralPipe,
        MatButton,
        MatFormField,
        MatInput,
        MatCardContent,
        MatTable,
        MatSort,
        MatSortHeader,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        DateFormatPipe,
        MatMiniFabButton,
        MatHeaderRow,
        MatHeaderRowDef,
        MatRow,
        MatRowDef,
        MatPaginator
    ],
    standalone: true
})
export class LocationsComponent implements OnInit, OnDestroy {

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    facilityId = '';
    facility: Observable<DocumentData>;
    dataSource: MatTableDataSource<DocumentData>;
    displayedColumns: string[] = ['name', 'created', 'actions'];
    breadcrumbs: BreadCrumb[];
    locationPluralMapping = {
        '=0': '0 Locations',
        '=1': '1 Location',
        other: '# Locations'
    };
    admin: any;
    private subscription: Subscription;
    private authSubscription: Subscription;
    private itemSubscription: Subscription;
    private facilitySubscription: Subscription;
    private facilityDocument: DocumentReference;

    constructor(private firestore: Firestore,
                private route: ActivatedRoute,
                private fireAuth: Auth,
                private snackBar: MatSnackBar) {
    }

    ngOnInit() {
        this.breadcrumbs = [
            {url: ['/'], title: 'Accounts'},
            {url: null, title: 'Facilities'},
            {url: null, title: 'Facility'},
        ];
        this.authSubscription = authState(this.fireAuth).subscribe(async (value) => {
            const result = await value.getIdTokenResult();
            this.admin = Boolean(result.claims.admin);
        });

        this.route.paramMap.subscribe(params => {
            this.facilityId = params.get('facilityId')!;
            const facilityDocRef = doc(this.firestore, 'facilities', this.facilityId);
            this.facilityDocument = facilityDocRef;
            this.facility = docData(facilityDocRef);
            this.facilitySubscription = this.facility.pipe(
                tap(data => {
                    this.breadcrumbs[1].url = ['../../' + data.accountId];
                    this.breadcrumbs[2].title = `Facility: ${data.name}`;
                })
            ).subscribe();
            const locationsColRef = collection(facilityDocRef, 'locations');
            const locationsQuery = query(locationsColRef, orderBy('name', 'asc'));
            this.subscription = collectionData(locationsQuery, {idField: 'id'}).pipe(
                catchError(e => throwError(() => e))
            ).subscribe(data => {
                this.dataSource = new MatTableDataSource(data);
                this.dataSource.sort = this.sort;
                this.dataSource.paginator = this.paginator;
            });
        });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        if (this.facilitySubscription && !this.facilitySubscription.closed) {
            this.facilitySubscription.unsubscribe();
        }
        this.authSubscription.unsubscribe();
    }

    search(event: any) {
        this.dataSource.filter = event.target.value.trim().toLowerCase();
    }

    deleteLocation(event: Event, location: Location) {
        event.stopPropagation();
        if (!this.admin) {
            this.snackBar.open('Permission Denied. Please contact an admin', null, {duration: 3000});
            return;
        }
        const itemsColRef = collection(this.firestore, 'items');
        const locationDocRef = doc(
            this.firestore,
            'facilities',
            this.facilityId,
            'locations',
            location.id
        );
        const itemsQuery = query(
            itemsColRef,
            where('location', '==', locationDocRef)
        );
        this.itemSubscription = from(getDocs(itemsQuery)).subscribe({
            next: snapshot => {
                if (snapshot.empty) {
                    if (confirm(`Are you sure you want to delete ${location.name}?`)) {
                        deleteDoc(locationDocRef)
                            .then(() => {
                                this.snackBar.open('Location Deleted', null, {duration: 3000});
                                this.itemSubscription.unsubscribe();
                            })
                            .catch(() => {
                                this.snackBar.open(`Error removing ${location.name}`, null, {duration: 3000});
                                this.itemSubscription.unsubscribe();
                            });
                    }
                } else {
                    this.snackBar.open(
                        `Please remove all items from location ${location.name} before deleting it`,
                        null,
                        {duration: 5000}
                    );
                }
            },
            error: () => {
                this.snackBar.open(`Error removing ${location.name}`, null, {duration: 5000});
            }
        });
    }

}
